import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

export default function AboutPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const personStructuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Melissa Thomas",
    "jobTitle": "CEO & Founder",
    "worksFor": {
      "@type": "Organization",
      "name": "Technelle.ai"
    },
    "description": "Marketing strategist with over 20 years of experience in AI-powered marketing, brand development, and workflow automation.",
    "url": "https://technelle.ai/about",
    "sameAs": [
      "https://www.linkedin.com"
    ]
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead 
        title="About Technelle | AI Marketing | Waukesha, WI"
        description="Meet Melissa Thomas, founder of Technelle.ai. With 20+ years of marketing expertise and 40,000+ hours of hands-on experience, Melissa helps Wisconsin businesses transform their marketing through AI-powered strategies and intelligent automation."
        keywords="Melissa Thomas, marketing consultant Waukesha, AI marketing expert, Wisconsin marketing consultant, marketing agency founder, business consultant Wisconsin, AI implementation expert"
        canonical="https://technelle.ai/about"
        ogTitle="About Technelle | AI Marketing | Waukesha, WI"
        ogDescription="Founded by Melissa Thomas, Technelle.ai combines deep marketing expertise with cutting-edge AI implementation. 20+ years experience, 1,000+ projects delivered, 40,000+ hours of expertise."
        ogUrl="https://technelle.ai/about"
        structuredData={personStructuredData}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="container mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle - AI Marketing & Consulting" className="h-14" />
          </Link>
          <div className="hidden lg:flex items-center gap-10">
            <Link to="/" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Home</Link>
            <Link to="/about" className="text-[15px] text-[#2196F3] transition-colors font-medium">About</Link>
            <Link to="/services" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Services</Link>
            <Link to="/industries" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Industries</Link>
            <Link to="/resources" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Resources</Link>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setMenuOpen(true)}
              className="hidden lg:block bg-[#2196F3] text-white px-6 sm:px-8 py-3 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors whitespace-nowrap cursor-pointer min-w-[44px] min-h-[44px]"
              aria-label="Book free strategy call"
            >
              Book Free Strategy Call
            </button>
            <button
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden w-11 h-11 flex items-center justify-center text-[#1a1a1a] cursor-pointer"
              aria-label="Open mobile menu"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Nav Drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-black/60 z-[200] lg:hidden" onClick={() => setMobileNavOpen(false)}>
          <div className="absolute top-0 right-0 w-72 h-full bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 h-20 border-b border-gray-100">
              <span className="text-[#1a1a1a] font-semibold text-[16px]">Menu</span>
              <button onClick={() => setMobileNavOpen(false)} className="w-10 h-10 flex items-center justify-center text-[#1a1a1a] cursor-pointer" aria-label="Close menu">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <nav className="flex flex-col px-6 py-8 gap-6">
              <Link to="/" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#2196F3] text-[16px] font-medium transition-colors">Home</Link>
              <Link to="/about" onClick={() => setMobileNavOpen(false)} className="text-[#2196F3] text-[16px] font-medium">About</Link>
              <Link to="/services" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#2196F3] text-[16px] font-medium transition-colors">Services</Link>
              <Link to="/resources" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#2196F3] text-[16px] font-medium transition-colors">Resources</Link>
            </nav>
            <div className="px-6 mt-auto pb-8">
              <button
                onClick={() => { setMobileNavOpen(false); setMenuOpen(true); }}
                className="w-full bg-[#2196F3] text-white py-4 rounded-md text-[15px] font-semibold hover:bg-[#1976D2] transition-colors cursor-pointer"
              >
                Book Free Strategy Call
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-end">
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
            <div className="p-8 sm:p-12">
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-[28px] sm:text-[32px] font-bold text-[#1a1a1a]">Let's talk —</h2>
                <button 
                  onClick={() => setMenuOpen(false)}
                  className="text-[#1a1a1a] hover:text-[#2196F3] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close contact form"
                >
                  <span className="text-[15px] font-medium">Close</span>
                </button>
              </div>
              <p className="text-[15px] text-gray-600">Please reach out to schedule your free strategy call at <a href="tel:4145309114" className="text-[#2196F3] hover:underline">414-530-9114</a> or <a href="mailto:melissa@technelle.ai" className="text-[#2196F3] hover:underline">melissa@technelle.ai</a></p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-50 to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/2fa4c0c7a257bec16203009fe93ef948.jpg"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width="800"
            height="1200"
          />
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-[40px] sm:text-[56px] lg:text-[76px] leading-[1.1] font-bold text-[#1a1a1a] mb-6 font-serif">
              We&apos;re <span className="relative inline-block text-[#2196F3]">
                Technelle
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 500 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M2 8C125 2 375 2 498 8" stroke="#2196F3" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </span>
            </h1>
            <p className="text-[20px] sm:text-[24px] text-gray-600 max-w-3xl mx-auto leading-relaxed">
              A Waukesha-based team of strategists, creatives, and technologists helping Wisconsin businesses grow through AI-powered marketing and intelligent automation.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="bg-white py-24">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">Our Story</div>
            <h2 className="text-[48px] font-serif font-bold text-[#1a1a1a] mb-8 leading-tight">
              20+ Years of Marketing <span className="relative inline-block text-[#2196F3]">
                Expertise
                <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 200 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M2 4C60 2 140 2 198 4" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
            </h2>
            
            <div className="space-y-6 text-[17px] text-gray-700 leading-relaxed mb-12">
              <p>
                Founded by <strong className="text-[#1a1a1a]">Melissa Thomas</strong> in Waukesha, Wisconsin, Technelle has been at the forefront of marketing innovation for over two decades. What began as a traditional marketing consultancy serving Southeast Wisconsin has evolved into a comprehensive AI-powered growth partner for businesses across multiple industries throughout Wisconsin and beyond.
              </p>
              <p>
                With <strong className="text-[#1a1a1a]">40,000+ hours of hands-on experience</strong> and thousands of successful projects delivered for Wisconsin businesses, we've helped companies transform their marketing from static campaigns into intelligent, adaptive systems that respond to customer behavior in real time.
              </p>
              <p>
                Today, we combine deep marketing expertise with cutting-edge AI implementation, delivering measurable results for small and mid-size Wisconsin businesses in hospitality, manufacturing, renewable energy, ecommerce, and beyond.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 border-t border-b border-gray-200 py-12">
              <div className="text-center">
                <div className="text-[48px] font-serif font-light text-[#1a1a1a] mb-2">20+</div>
                <p className="text-[16px] text-gray-700">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-[48px] font-serif font-light text-[#1a1a1a] mb-2">40K+</div>
                <p className="text-[16px] text-gray-700">Hours Delivered</p>
              </div>
              <div className="text-center">
                <div className="text-[48px] font-serif font-light text-[#1a1a1a] mb-2">1000s</div>
                <p className="text-[16px] text-gray-700">Projects Completed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center">What Drives Us</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-lightbulb-line text-3xl text-blue-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Innovation</h3>
              <p className="text-gray-600">We stay ahead of the curve, constantly exploring new AI tools and marketing strategies to give our Wisconsin clients a competitive edge.</p>
            </div>

            <div className="bg-white rounded-lg p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-line text-3xl text-blue-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Partnership</h3>
              <p className="text-gray-600">Your success is our success. We work as an extension of your Wisconsin team, invested in your long-term growth.</p>
            </div>

            <div className="bg-white rounded-lg p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-bar-chart-line text-3xl text-blue-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Results</h3>
              <p className="text-gray-600">We're obsessed with measurable outcomes for Wisconsin businesses. Every strategy is designed to move the needle on your bottom line.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership: Melissa Thomas */}
      <section className="bg-white py-24">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">Leadership</div>
              <h2 className="text-[48px] font-serif font-bold text-[#1a1a1a]">Meet Our Founder</h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <img 
                  src="https://static.readdy.ai/image/cb419a93222bfd6610832c3481bbaa16/2c67dd839119d38c5dbf08ae5a5475da.png"
                  alt="Melissa Thomas, CEO & Founder of Technelle"
                  className="w-full max-w-md mx-auto rounded-2xl"
                  loading="lazy"
                  decoding="async"
                  width="448"
                  height="560"
                />
              </div>
              <div>
                <h3 className="text-[36px] font-serif font-bold text-[#1a1a1a] mb-3">Melissa Thomas</h3>
                <div className="text-[18px] text-[#2196F3] font-medium mb-6">CEO & Founder</div>
                
                <div className="space-y-4 text-[16px] text-gray-700 leading-relaxed mb-8">
                  <p>
                    With over 20 years of experience in marketing strategy and execution, Melissa has helped hundreds of Wisconsin businesses transform their marketing from cost centers into revenue engines.
                  </p>
                  <p>
                    Her expertise spans brand development, demand generation, AI implementation, and workflow automation. She's particularly passionate about making sophisticated marketing strategies and AI technology accessible to small and mid-size businesses throughout Wisconsin.
                  </p>
                  <p>
                    Beyond traditional marketing, Melissa brings extensive hands-on experience in <strong className="text-[#1a1a1a]">tradeshow management, corporate event planning, ABM campaigns, and wedding coordination</strong>—giving her a unique perspective on integrated marketing and customer experience for Wisconsin clients.
                  </p>
                  <div className="bg-blue-50 border-l-4 border-[#2196F3] rounded-r-xl p-6 mt-2">
                    <p className="text-[16px] text-gray-700 leading-relaxed italic">
                      "I didn't just study AI marketing — I used it to build TeenWorkHub, a national marketplace connecting teens to local jobs, from zero to a 300+ person waitlist with institutional interest from school districts across the US and Canada — with no paid marketing budget. That's the power I now bring to my clients."
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-[14px] font-semibold text-[#2196F3]">— Melissa Thomas</span>
                      <span className="text-[14px] text-gray-500">Founder, Technelle</span>
                    </div>
                  </div>
                </div>

                <a 
                  href="https://www.linkedin.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-[#0077B5] text-white px-6 py-3 rounded-md hover:bg-[#006399] transition-colors cursor-pointer"
                  aria-label="Connect with Melissa Thomas on LinkedIn"
                >
                  <i className="fab fa-linkedin-in text-xl" aria-hidden="true"></i>
                  <div className="text-left">
                    <div className="text-[11px] opacity-90">Connect with Melissa on</div>
                    <div className="text-[13px] font-semibold">LinkedIn</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process Framework */}
      <section className="bg-gray-50 py-24">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-[48px] font-serif font-bold text-[#1a1a1a] mb-4">
                Our <span className="relative inline-block text-[#2196F3]">
                  Framework
                  <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 220 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M2 4C60 2 160 2 218 4" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </span>
              </h2>
              <p className="text-[18px] text-gray-600 max-w-3xl mx-auto leading-relaxed">
                A proven three-phase approach that delivers sustainable growth for Wisconsin businesses
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-10 text-center hover:shadow-xl transition-all">
                <div className="w-20 h-20 bg-[#2196F3] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white text-[32px] font-bold">1</span>
                </div>
                <h3 className="text-[28px] font-serif font-bold text-[#1a1a1a] mb-4">Strategy</h3>
                <p className="text-[16px] text-gray-700 leading-relaxed mb-6">
                  We start by understanding your Wisconsin business, audience, and competitive landscape. Through discovery sessions and data analysis, we identify opportunities and build a strategic roadmap.
                </p>
                <ul className="text-left space-y-2 text-[14px] text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span>Discovery & audience research</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span>Competitive positioning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span>Strategic roadmap development</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-10 text-center hover:shadow-xl transition-all">
                <div className="w-20 h-20 bg-[#2196F3] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white text-[32px] font-bold">2</span>
                </div>
                <h3 className="text-[28px] font-serif font-bold text-[#1a1a1a] mb-4">Execution</h3>
                <p className="text-[16px] text-gray-700 leading-relaxed mb-6">
                  Strategy comes to life through expert execution. We build campaigns, implement AI solutions, and create content that engages your Wisconsin audience and drives conversions.
                </p>
                <ul className="text-left space-y-2 text-[14px] text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span>Campaign development & launch</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span>AI tool implementation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span>Content creation & design</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-10 text-center hover:shadow-xl transition-all">
                <div className="w-20 h-20 bg-[#2196F3] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white text-[32px] font-bold">3</span>
                </div>
                <h3 className="text-[28px] font-serif font-bold text-[#1a1a1a] mb-4">Optimization</h3>
                <p className="text-[16px] text-gray-700 leading-relaxed mb-6">
                  Continuous improvement through data analysis and testing. We monitor performance, identify opportunities, and refine strategies to maximize ROI.
                </p>
                <ul className="text-left space-y-2 text-[14px] text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span>Performance tracking & reporting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span>A/B testing & refinement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span>Continuous improvement</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where We Work — contextual service-area links */}
      <section className="bg-gray-50 py-16 border-t border-gray-100">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-3">Where We Work</div>
              <h2 className="text-[32px] font-serif font-bold text-[#1a1a1a] mb-3">Serving Wisconsin Businesses Statewide</h2>
              <p className="text-[16px] text-gray-600 max-w-2xl mx-auto">
                Based in Waukesha, Technelle delivers AI marketing and consulting to businesses across Wisconsin — from Milwaukee's manufacturing corridor to Madison's tech ecosystem to Green Bay's industrial base.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <Link to="/service-areas/milwaukee" className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
                <div className="text-[18px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">Milwaukee, WI</div>
                <p className="text-[14px] text-gray-600 leading-relaxed mb-3">Wisconsin's largest market — manufacturing, healthcare, trade services, and professional firms.</p>
                <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">View Milwaukee services <i className="ri-arrow-right-line"></i></span>
              </Link>
              <Link to="/service-areas/madison" className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
                <div className="text-[18px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">Madison, WI</div>
                <p className="text-[14px] text-gray-600 leading-relaxed mb-3">Wisconsin's capital and innovation hub — tech startups, UW-adjacent businesses, and professional services.</p>
                <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">View Madison services <i className="ri-arrow-right-line"></i></span>
              </Link>
              <Link to="/service-areas/green-bay" className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
                <div className="text-[18px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">Green Bay, WI</div>
                <p className="text-[14px] text-gray-600 leading-relaxed mb-3">Northeast Wisconsin's hub — manufacturing, hospitality, sports & entertainment, and retail.</p>
                <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">View Green Bay services <i className="ri-arrow-right-line"></i></span>
              </Link>
            </div>
            <div className="text-center mt-8">
              <Link to="/service-areas" className="inline-flex items-center gap-2 text-[#2196F3] text-[14px] font-semibold hover:underline">
                See all Wisconsin service areas <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-24">
        <div className="container mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-[36px] sm:text-[48px] lg:text-[56px] font-serif font-bold text-[#1a1a1a] mb-6 leading-tight">
            Ready to Work<br />
            <span className="relative inline-block text-[#2196F3]">
              Together
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M2 6C60 2 140 2 198 6" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>?
          </h2>
          <p className="text-[20px] text-gray-600 mb-10 max-w-2xl mx-auto">Schedule a free strategy call to discuss your Wisconsin business goals and explore how we can help you grow.</p>
          <button 
            onClick={() => setMenuOpen(true)}
            className="bg-[#2196F3] text-white px-10 py-4 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors cursor-pointer min-w-[44px] min-h-[44px]"
          >
            <span>Book Your Free Strategy Call</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle" className="h-12" width="48" height="48" loading="lazy" decoding="async" />
              </div>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Links</h5>
              <nav className="space-y-3">
                <Link to="/" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Home</Link>
                <Link to="/about" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">About</Link>
                <Link to="/services" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Services</Link>
                <Link to="/industries" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Industries</Link>
                <Link to="/resources" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Resources</Link>
                <Link to="/how-we-work" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">How We Work</Link>
                <Link to="/case-studies" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Case Studies</Link>
                <Link to="/faq" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">FAQ</Link>
                <Link to="/service-areas" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Service Areas</Link>
                <Link to="/contact" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Contact</Link>
              </nav>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Contact</h5>
              <div className="space-y-3">
                <a href="tel:4145309114" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">414-530-9114</a>
                <a href="mailto:melissa@technelle.ai" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">melissa@technelle.ai</a>
              </div>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Legal</h5>
              <nav className="space-y-3">
                <Link to="/privacy-policy" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Privacy Policy</Link>
                <Link to="/terms" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Terms</Link>
              </nav>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="text-[14px] text-gray-400">© 2025 Technelle. All rights reserved.</div>

            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
